/**
 * A reader for CITATION.cff files — the subset of YAML they use, and the keys a
 * citation needs.
 *
 * CITATION.cff (Citation File Format 1.2.0; the schema guide in
 * github.com/citation-file-format/citation-file-format) is YAML: nested maps, lists of
 * maps for authors and identifiers, quoted or plain scalars, comments, and the odd
 * block scalar for an abstract. The parser below reads exactly that by indentation and
 * never throws — a line it does not understand is skipped, so a malformed file yields
 * whatever could be read, and the form stays editable either way. Adding a YAML
 * library for one tool would put far more code in the page than this.
 */

export type CffNode = string | CffNode[] | CffMap;
export interface CffMap {
  [key: string]: CffNode;
}

interface Line {
  indent: number;
  text: string;
}

const RE_INDENT = /^ */;
const RE_KEY = /^([A-Za-z0-9_-]+|"[^"]*"|'[^']*'):(?:\s+(.*))?$/;
const RE_BLOCK = /^[|>][+-]?\d*$/;
const RE_LIST_ITEM = /^-(?:\s+(.*))?$/;
const RE_TRAILING_COMMENT = /\s+#.*$/;
const RE_DOUBLE_ESCAPE = /\\(["\\/nt])/g;
const RE_NEWLINE = /\r?\n/;

const ESCAPES: Record<string, string> = {
  '"': '"',
  "\\": "\\",
  "/": "/",
  n: "\n",
  t: "\t",
};

/** Strip a comment that is not inside quotes. */
function withoutComment(text: string): string {
  if (text.startsWith('"') || text.startsWith("'")) {
    const quote = text[0];
    const close = text.indexOf(quote, 1);
    if (close > 0) {
      return (
        text.slice(0, close + 1) +
        text.slice(close + 1).replace(RE_TRAILING_COMMENT, "")
      );
    }
    return text;
  }
  return text.replace(RE_TRAILING_COMMENT, "");
}

function toLines(source: string): Line[] {
  const lines: Line[] = [];
  for (const raw of source.split(RE_NEWLINE)) {
    const trimmed = raw.trim();
    if (
      trimmed === "" ||
      trimmed.startsWith("#") ||
      trimmed === "---" ||
      trimmed === "..."
    ) {
      lines.push({ indent: -1, text: raw });
      continue;
    }
    lines.push({ indent: raw.match(RE_INDENT)?.[0].length ?? 0, text: raw });
  }
  return lines;
}

/** A scalar's value: quotes removed, escapes resolved, a flow list read as a list. */
export function scalar(raw: string): CffNode {
  const text = withoutComment(raw.trim()).trim();
  if (text.startsWith('"') && text.endsWith('"') && text.length >= 2) {
    return text
      .slice(1, -1)
      .replace(
        RE_DOUBLE_ESCAPE,
        (_match, char: string) => ESCAPES[char] ?? char
      );
  }
  if (text.startsWith("'") && text.endsWith("'") && text.length >= 2) {
    return text.slice(1, -1).replaceAll("''", "'");
  }
  if (text.startsWith("[") && text.endsWith("]")) {
    return text
      .slice(1, -1)
      .split(",")
      .map((item) => scalar(item) as string)
      .filter((item) => item !== "");
  }
  return text;
}

class CffParser {
  private index = 0;
  private readonly lines: Line[];

  constructor(source: string) {
    this.lines = toLines(source);
  }

  /** The next line with content, skipping blanks and comments. */
  private peek(): Line | undefined {
    while (
      this.index < this.lines.length &&
      this.lines[this.index].indent < 0
    ) {
      this.index += 1;
    }
    return this.lines[this.index];
  }

  parseDocument(): CffMap {
    const first = this.peek();
    if (!first) {
      return {};
    }
    const node = this.parseNode(first.indent);
    return typeof node === "object" && !Array.isArray(node) ? node : {};
  }

  private parseNode(indent: number): CffNode {
    const line = this.peek();
    if (!line) {
      return "";
    }
    return RE_LIST_ITEM.test(line.text.trim())
      ? this.parseList(line.indent)
      : this.parseMap(indent);
  }

  /** Lines more indented than `indent`, joined — a literal (|) or folded (>) scalar. */
  private blockScalar(indent: number, folded: boolean): string {
    const parts: string[] = [];
    while (this.index < this.lines.length) {
      const line = this.lines[this.index];
      if (line.indent >= 0 && line.indent <= indent) {
        break;
      }
      parts.push(line.text.trim());
      this.index += 1;
    }
    return parts.join(folded ? " " : "\n").trim();
  }

  private parseValue(rest: string, indent: number): CffNode {
    if (RE_BLOCK.test(rest)) {
      return this.blockScalar(indent, rest.startsWith(">"));
    }
    if (rest !== "") {
      // A plain or quoted scalar may continue on more-indented lines.
      const parts = [rest];
      for (
        let next = this.peek();
        next && next.indent > indent;
        next = this.peek()
      ) {
        parts.push(next.text.trim());
        this.index += 1;
      }
      return scalar(parts.join(" "));
    }
    const next = this.peek();
    if (!next) {
      return "";
    }
    // "authors:" may be followed by "- …" at the same indentation as the key.
    if (
      next.indent > indent ||
      (next.indent === indent && RE_LIST_ITEM.test(next.text.trim()))
    ) {
      return this.parseNode(next.indent);
    }
    return "";
  }

  private parseMap(indent: number): CffMap {
    const map: CffMap = {};
    for (
      let line = this.peek();
      line && line.indent === indent;
      line = this.peek()
    ) {
      const match = RE_KEY.exec(line.text.trim());
      if (!match) {
        this.index += 1;
        continue;
      }
      this.index += 1;
      const key = scalar(match[1]) as string;
      map[key] = this.parseValue((match[2] ?? "").trim(), indent);
    }
    return map;
  }

  private parseList(indent: number): CffNode[] {
    const list: CffNode[] = [];
    for (
      let line = this.peek();
      line && line.indent === indent;
      line = this.peek()
    ) {
      const match = RE_LIST_ITEM.exec(line.text.trim());
      if (!match) {
        break;
      }
      const rest = (match[1] ?? "").trim();
      if (rest === "") {
        this.index += 1;
        list.push(this.parseValue("", indent));
        continue;
      }
      if (RE_KEY.test(rest)) {
        // "- family-names: Druskat": the item is a map indented past the dash.
        const childIndent = indent + (line.text.trim().length - rest.length);
        this.lines[this.index] = {
          indent: childIndent,
          text: `${" ".repeat(childIndent)}${rest}`,
        };
        list.push(this.parseMap(childIndent));
        continue;
      }
      this.index += 1;
      list.push(scalar(rest));
    }
    return list;
  }
}

/** Parse a CITATION.cff file. Never throws; returns {} for anything unreadable. */
export function parseCff(source: string): CffMap {
  try {
    return new CffParser(source).parseDocument();
  } catch {
    return {};
  }
}

/** A child map, or undefined. */
export function cffMap(node: CffNode | undefined): CffMap | undefined {
  return node && typeof node === "object" && !Array.isArray(node)
    ? node
    : undefined;
}

/** A child list, or []. */
export function cffList(node: CffNode | undefined): CffNode[] {
  return Array.isArray(node) ? node : [];
}

/** A child string, or "". */
export function cffText(node: CffNode | undefined): string {
  return typeof node === "string" ? node.trim() : "";
}
